/**
 * DtcShopifySalesChart - Bar chart showing daily Shopify sales revenue
 * 
 * CRITICAL: Uses the SAME data source (kpiData.trend) as Daily Sales Trend table
 * to maintain single source of truth. No separate API calls.
 */

import React, { useState, useMemo } from 'react'
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import { BarChart3 } from 'lucide-react'
import { DtcTrendDataPoint } from '../lib/dtc-data-service'

interface ExtendedTrendDataPoint extends DtcTrendDataPoint {
  ly_revenue?: number | null
  ly_orders?: number | null
  ly_units?: number | null
  lw_revenue?: number | null
  lw_orders?: number | null
  lw_units?: number | null
}

interface DtcShopifySalesChartProps {
  data: ExtendedTrendDataPoint[]
  platformColor?: string
  platformName?: string
  className?: string
  showLyComparison?: boolean
  showLwComparison?: boolean
}

const formatCurrency = (value: number) => {
  if (!Number.isFinite(value)) return '$0'
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`
  }
  if (Math.abs(value) >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatCurrencyFull = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatDateLabel = (dateString: string) => {
  try {
    // Handle both date formats: "2025-11-07" and "2025-11-07T00:00:00.000Z"
    // CRITICAL: Always use 'Z' suffix to ensure UTC parsing for consistent dates
    const dateStr = dateString.includes('T') ? dateString : dateString + 'T00:00:00Z'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  } catch {
    return dateString
  }
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    // CRITICAL: Always use 'Z' suffix to ensure UTC parsing for consistent dates
    const dateStr = data.date.includes('T') ? data.date : data.date + 'T00:00:00Z'
    const date = new Date(dateStr)
    const formattedDate = date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    })

    // Check if this is a missing data day
    const isMissing = data.isMissing === true
    
    // Calculate LW comparison
    const hasLwData = data.lw_revenue !== null && data.lw_revenue !== undefined && data.lw_revenue > 0
    const lwRevenuePct = hasLwData && data.lw_revenue > 0 
      ? ((data.total_revenue - data.lw_revenue) / data.lw_revenue) * 100 
      : null
    
    // Calculate LY comparison
    const hasLyData = data.ly_revenue !== null && data.ly_revenue !== undefined && data.ly_revenue > 0
    const lyRevenuePct = hasLyData && data.ly_revenue > 0 
      ? ((data.total_revenue - data.ly_revenue) / data.ly_revenue) * 100 
      : null

    return (
      <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-4 min-w-[220px]">
        <p className="text-sm font-semibold text-gray-900 mb-3">{formattedDate}</p>
        {isMissing ? (
          <div className="text-center py-2">
            <span className="text-sm text-gray-500 font-medium">N/A</span>
            <p className="text-xs text-gray-400 mt-1">No data available</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">Revenue:</span>
              <span className="text-sm font-bold text-green-600">
                {formatCurrencyFull(data.total_revenue)}
              </span>
            </div>
            {/* LW Comparison */}
            {hasLwData && (
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-blue-500">LW:</span>
                <span className="text-gray-500">
                  {formatCurrencyFull(data.lw_revenue)}
                  {lwRevenuePct !== null && (
                    <span className={`ml-1 font-semibold ${lwRevenuePct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ({lwRevenuePct >= 0 ? '+' : ''}{lwRevenuePct.toFixed(0)}%)
                    </span>
                  )}
                </span>
              </div>
            )}
            {/* LY Comparison */}
            {hasLyData && (
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-gray-400">LY:</span>
                <span className="text-gray-500">
                  {formatCurrencyFull(data.ly_revenue)}
                  {lyRevenuePct !== null && (
                    <span className={`ml-1 font-semibold ${lyRevenuePct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ({lyRevenuePct >= 0 ? '+' : ''}{lyRevenuePct.toFixed(0)}%)
                    </span>
                  )}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">Orders:</span>
              <span className="text-sm font-semibold text-gray-900">
                {data.total_orders.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">Units:</span>
              <span className="text-sm font-semibold text-gray-900">
                {data.total_units.toLocaleString()}
              </span>
            </div>
            <div className="border-t border-gray-200 mt-2 pt-2 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">AOV:</span>
              <span className="text-sm font-bold text-gray-900">
                {formatCurrencyFull(data.avg_order_value)}
              </span>
            </div>
          </div>
        )}
      </div>
    )
  }
  return null
}

// Extended data point that can represent missing data
interface ChartDataPoint extends ExtendedTrendDataPoint {
  isMissing?: boolean
}

export function DtcShopifySalesChart({ 
  data, 
  platformColor = '#95BF47', 
  platformName = 'Shopify',
  className = '',
  showLyComparison = true,
  showLwComparison = true
}: DtcShopifySalesChartProps) {
  // State for toggling comparison lines
  const [showLY, setShowLY] = useState(showLyComparison)
  const [showLW, setShowLW] = useState(showLwComparison)
  
  // Check if we have LY/LW data
  const hasLyData = useMemo(() => data.some(d => d.ly_revenue !== null && d.ly_revenue !== undefined && d.ly_revenue > 0), [data])
  const hasLwData = useMemo(() => data.some(d => d.lw_revenue !== null && d.lw_revenue !== undefined && d.lw_revenue > 0), [data])
  
  // Sort data and fill in missing dates
  const sortedData = React.useMemo((): ChartDataPoint[] => {
    if (!data || data.length === 0) return []
    
    // Sort by date
    // CRITICAL: Always use 'Z' suffix to ensure UTC parsing for consistent sorting
    const sorted = [...data].sort((a, b) => {
      const aDateStr = a.date.includes('T') ? a.date : a.date + 'T00:00:00Z'
      const bDateStr = b.date.includes('T') ? b.date : b.date + 'T00:00:00Z'
      return new Date(aDateStr).getTime() - new Date(bDateStr).getTime()
    })
    
    // Create a map of existing dates
    const dateMap = new Map<string, DtcTrendDataPoint>()
    sorted.forEach(point => {
      const dateKey = point.date.split('T')[0] // Normalize to YYYY-MM-DD
      dateMap.set(dateKey, point)
    })
    
    // Get date range (first to last) - use UTC to avoid timezone issues
    const firstDateStr = sorted[0].date.split('T')[0]
    const lastDateStr = sorted[sorted.length - 1].date.split('T')[0]
    
    // Parse as UTC dates
    const [fy, fm, fd] = firstDateStr.split('-').map(Number)
    const [ly, lm, ld] = lastDateStr.split('-').map(Number)
    const firstDate = new Date(Date.UTC(fy, fm - 1, fd))
    const lastDate = new Date(Date.UTC(ly, lm - 1, ld))
    
    // Fill in missing dates
    const filledData: ChartDataPoint[] = []
    const currentDate = new Date(firstDate)
    
    while (currentDate <= lastDate) {
      const dateKey = currentDate.toISOString().split('T')[0]
      const existingPoint = dateMap.get(dateKey)
      
      if (existingPoint) {
        filledData.push({ ...existingPoint, isMissing: false })
      } else {
        // Add placeholder for missing date
        filledData.push({
          date: dateKey,
          total_revenue: 0,
          total_orders: 0,
          total_units: 0,
          avg_order_value: 0,
          isMissing: true
        })
      }
      
      // Add one day in UTC
      currentDate.setUTCDate(currentDate.getUTCDate() + 1)
    }
    
    return filledData
  }, [data])

  if (!data || data.length === 0) {
    return (
      <div className={`bg-white rounded-lg border-2 border-gray-200 shadow-sm p-8 text-center ${className}`}>
        <BarChart3 className="w-12 h-12 mx-auto text-gray-400 mb-3" />
        <p className="text-sm text-gray-600">No sales data available for this period</p>
      </div>
    )
  }

  // LY line color (darker/muted version)
  const lyColor = '#9CA3AF' // Gray
  const lwColor = '#60A5FA' // Blue
  
  return (
    <div className={`bg-white rounded-lg border-2 border-gray-200 shadow-sm ${className}`}>
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" style={{ color: platformColor }} />
              {platformName} Sales Trend
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Daily revenue performance • {sortedData.length} days shown
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Total Revenue</div>
            <div className="text-lg font-bold" style={{ color: platformColor }}>
              {formatCurrencyFull(sortedData.filter(p => !p.isMissing).reduce((sum, point) => sum + point.total_revenue, 0))}
            </div>
          </div>
        </div>
        
        {/* Comparison Toggles */}
        {(hasLyData || hasLwData) && (
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs font-medium text-gray-600">Compare:</span>
            {hasLwData && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showLW}
                  onChange={(e) => setShowLW(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700 flex items-center gap-1">
                  <span className="w-3 h-0.5 rounded" style={{ backgroundColor: lwColor }} />
                  Last Week
                </span>
              </label>
            )}
            {hasLyData && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showLY}
                  onChange={(e) => setShowLY(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-gray-500 focus:ring-gray-500"
                />
                <span className="text-xs text-gray-700 flex items-center gap-1">
                  <span className="w-3 h-0.5 rounded" style={{ backgroundColor: lyColor }} />
                  Last Year
                </span>
              </label>
            )}
          </div>
        )}
      </div>

      <div className="px-4 py-4 h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={sortedData} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
            <defs>
              <pattern id="missing-pattern" patternUnits="userSpaceOnUse" width="8" height="8">
                <path d="M-2,2 l4,-4 M0,8 l8,-8 M6,10 l4,-4" stroke="#d1d5db" strokeWidth="2"/>
              </pattern>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateLabel}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tickFormatter={formatCurrency}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Current Period Bars */}
            <Bar
              dataKey="total_revenue"
              radius={[6, 6, 0, 0]}
              maxBarSize={50}
              name="This Period"
              shape={(props: any) => {
                const { x, y, width, height, payload } = props
                const isMissing = payload?.isMissing
                // For missing data, show a small dashed bar
                if (isMissing) {
                  return (
                    <g>
                      <rect
                        x={x}
                        y={y + height - 20}
                        width={width}
                        height={20}
                        fill="url(#missing-pattern)"
                        stroke="#d1d5db"
                        strokeWidth={1}
                        strokeDasharray="4 2"
                        rx={4}
                        ry={4}
                      />
                      <text
                        x={x + width / 2}
                        y={y + height - 6}
                        textAnchor="middle"
                        fill="#9ca3af"
                        fontSize={9}
                        fontWeight="500"
                      >
                        N/A
                      </text>
                    </g>
                  )
                }
                // Normal bar
                return (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={platformColor}
                    rx={6}
                    ry={6}
                  />
                )
              }}
            />
            
            {/* Last Week Line */}
            {showLW && hasLwData && (
              <Line
                type="monotone"
                dataKey="lw_revenue"
                stroke={lwColor}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: lwColor, r: 3 }}
                name="Last Week"
                connectNulls
              />
            )}
            
            {/* Last Year Line */}
            {showLY && hasLyData && (
              <Line
                type="monotone"
                dataKey="ly_revenue"
                stroke={lyColor}
                strokeWidth={2}
                strokeDasharray="3 3"
                dot={{ fill: lyColor, r: 3 }}
                name="Last Year"
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="px-6 pb-4 flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: platformColor }} />
            <span>This Period</span>
          </div>
          {showLW && hasLwData && (
            <div className="flex items-center gap-1">
              <div className="w-4 h-0.5 rounded" style={{ backgroundColor: lwColor }} />
              <span>Last Week</span>
            </div>
          )}
          {showLY && hasLyData && (
            <div className="flex items-center gap-1">
              <div className="w-4 h-0.5 rounded" style={{ backgroundColor: lyColor }} />
              <span>Last Year</span>
            </div>
          )}
        </div>
        <div>
          Data source: {platformName} Store • Updated in real-time
        </div>
      </div>
    </div>
  )
}

export default DtcShopifySalesChart

